import { useEffect, useState } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Activity,
  Mail,
  MessageSquare,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Phone,
  Shield,
  ShieldOff,
  Send,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
      }`}
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const CHANNEL_BADGES = {
  email: { label: "Email", className: "bg-blue-100 text-blue-700" },
  sms: { label: "SMS", className: "bg-emerald-100 text-emerald-700" },
};

const KIND_LABELS = {
  login_success: "Login exitoso",
  login_fail: "Login fallido",
  otp_verify_success: "OTP correcto",
  otp_verify_fail: "OTP incorrecto",
  logout: "Cierre de sesión",
  "2fa_toggled": "2FA modificado",
  "2fa_method_changed": "Método cambiado",
  device_revoked: "Dispositivo revocado",
  phone_verified: "Teléfono verificado",
  phone_removed: "Teléfono eliminado",
};

export default function AdminDiagnosticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // 'email' | 'sms' | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/diagnostics/overview");
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el diagnóstico");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendTest = async (channel) => {
    setSending(channel);
    try {
      const { data } = await api.post("/admin/diagnostics/send-test", { channel });
      const target = data.target_masked || "destino";
      if (channel === "email") {
        toast.success(`Email de prueba enviado a ${target}. Revisa bandeja + spam.`);
      } else {
        toast.success(`SMS de prueba enviado a ${target}. Twilio status: ${data.twilio_status}.`);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar");
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center" data-testid="admin-diag-loading">
        <Loader2 className="w-6 h-6 animate-spin text-terracotta mx-auto" />
      </div>
    );
  }
  if (!data) return null;

  const { admin, providers, recent_otps, recent_auth_events } = data;

  return (
    <div className="space-y-5" data-testid="admin-diagnostics-tab">
      {/* Top action bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-heading font-semibold text-agave flex items-center gap-2">
            <Activity className="w-5 h-5 text-terracotta" />
            Diagnóstico 2FA
          </h2>
          <p className="text-sm text-neutral-600">
            Verifica si los códigos por email y SMS se están entregando.
          </p>
        </div>
        <Button variant="outline" onClick={load} size="sm" data-testid="admin-diag-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refrescar
        </Button>
      </div>

      {/* Admin account state */}
      <section className="card-base" data-testid="admin-diag-account">
        <h3 className="font-heading font-semibold text-lg text-agave mb-3">Tu cuenta admin</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-neutral-500" />
            <span className="text-neutral-600">Email:</span>
            <span className="font-medium text-agave">{admin.email_masked}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-neutral-500" />
            <span className="text-neutral-600">Teléfono:</span>
            <span className="font-medium text-agave">
              {admin.phone_masked || "no configurado"}
            </span>
            {admin.phone_verified && <Badge className="bg-emerald-600 text-white border-0">verificado</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {admin.two_fa_enabled ? (
              <Shield className="w-4 h-4 text-emerald-600" />
            ) : (
              <ShieldOff className="w-4 h-4 text-amber-600" />
            )}
            <span className="text-neutral-600">2FA:</span>
            <span className="font-medium">{admin.two_fa_enabled ? "activado" : "desactivado"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-neutral-600">Canal por defecto:</span>
            <Badge className={CHANNEL_BADGES[admin.two_fa_method]?.className || "bg-neutral-200 text-neutral-700"}>
              {CHANNEL_BADGES[admin.two_fa_method]?.label || admin.two_fa_method}
            </Badge>
          </div>
        </div>
      </section>

      {/* Provider config */}
      <section className="card-base" data-testid="admin-diag-providers">
        <h3 className="font-heading font-semibold text-lg text-agave mb-3">Estado de los proveedores</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <StatusPill ok={providers.resend_configured} label="Resend (email)" />
          <StatusPill ok={providers.twilio_account_configured && providers.twilio_api_key_configured} label="Twilio Account" />
          <StatusPill ok={!!providers.twilio_verify_service_sid} label="Twilio Verify Service" />
          <StatusPill ok={providers.emergent_llm_configured} label="Gemini (Emergent LLM)" />
          {providers.admin_2fa_bypass && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5" />
              ADMIN_2FA_BYPASS activo
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 space-y-1">
          {providers.sender_email && (
            <p>Remitente email: <code className="bg-neutral-100 px-1.5 py-0.5 rounded">{providers.sender_email}</code></p>
          )}
          {providers.twilio_verify_service_sid && (
            <p>Verify Service SID: <code className="bg-neutral-100 px-1.5 py-0.5 rounded">{providers.twilio_verify_service_sid}</code></p>
          )}
          {providers.admin_phone_env && (
            <p className="text-emerald-700">✓ ADMIN_PHONE configurado en env (se siembra en cada inicio)</p>
          )}
        </div>
      </section>

      {/* Send test */}
      <section className="card-base" data-testid="admin-diag-test">
        <h3 className="font-heading font-semibold text-lg text-agave mb-1">Enviar mensaje de prueba</h3>
        <p className="text-sm text-neutral-600 mb-4">
          Envía un mensaje real a tu propio email o teléfono para confirmar la entrega.
        </p>
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={() => sendTest("email")}
            disabled={sending !== null || !providers.resend_configured}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="admin-diag-send-email"
          >
            {sending === "email" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            Enviar email de prueba
          </Button>
          <Button
            onClick={() => sendTest("sms")}
            disabled={sending !== null || !admin.phone_verified || !providers.twilio_account_configured}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="admin-diag-send-sms"
          >
            {sending === "sms" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
            Enviar SMS de prueba
          </Button>
        </div>
        {!admin.phone_verified && (
          <p className="text-xs text-amber-700 mt-2">
            ⚠ El SMS de prueba requiere un teléfono verificado en la cuenta admin.
          </p>
        )}
      </section>

      {/* Recent OTPs */}
      <section className="card-base" data-testid="admin-diag-otps">
        <h3 className="font-heading font-semibold text-lg text-agave mb-3">
          Últimos códigos OTP ({recent_otps.length})
        </h3>
        {recent_otps.length === 0 ? (
          <p className="text-sm text-neutral-500 py-4">Aún no hay OTPs registrados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-neutral-500 border-b border-neutral-200">
                <tr>
                  <th className="text-left py-2 px-2">Canal</th>
                  <th className="text-left py-2 px-2">Destino</th>
                  <th className="text-left py-2 px-2">Estado</th>
                  <th className="text-left py-2 px-2">Intentos</th>
                  <th className="text-left py-2 px-2">Edad</th>
                </tr>
              </thead>
              <tbody>
                {recent_otps.map((o) => {
                  const used = o.used;
                  const expired = !used && o.expires_at && new Date(o.expires_at) < new Date();
                  const target = o.user_phone_masked || o.target_phone_masked || o.user_email_masked || "—";
                  return (
                    <tr key={o.otp_token} className="border-b border-neutral-100 last:border-0">
                      <td className="py-2 px-2">
                        <Badge className={CHANNEL_BADGES[o.channel]?.className || "bg-neutral-200 text-neutral-700"}>
                          {CHANNEL_BADGES[o.channel]?.label || o.channel}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 text-neutral-700 truncate max-w-[180px]">{target}</td>
                      <td className="py-2 px-2">
                        {used ? (
                          <span className="text-emerald-700 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Usado
                          </span>
                        ) : expired ? (
                          <span className="text-neutral-500 inline-flex items-center gap-1">
                            <XCircle className="w-3.5 h-3.5" /> Expirado
                          </span>
                        ) : (
                          <span className="text-blue-700 inline-flex items-center gap-1">
                            <Send className="w-3.5 h-3.5" /> Pendiente
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-neutral-600">{o.attempts}</td>
                      <td className="py-2 px-2 text-neutral-500">hace {timeAgo(o.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recent auth events */}
      <section className="card-base" data-testid="admin-diag-events">
        <h3 className="font-heading font-semibold text-lg text-agave mb-3">
          Últimos eventos de autenticación ({recent_auth_events.length})
        </h3>
        {recent_auth_events.length === 0 ? (
          <p className="text-sm text-neutral-500 py-4">Sin eventos.</p>
        ) : (
          <ul className="divide-y divide-neutral-200">
            {recent_auth_events.map((e) => (
              <li key={e.event_id} className="py-2 flex items-start gap-3 text-sm">
                <span
                  className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                    e.success ? "bg-emerald-500" : "bg-red-500"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">{KIND_LABELS[e.kind] || e.kind}</p>
                  <p className="text-xs text-neutral-500 truncate">
                    {[e.city, e.country].filter(Boolean).join(", ") || e.ip || "—"}
                    {e.browser ? ` · ${e.browser}` : ""}
                    {e.os ? ` · ${e.os}` : ""}
                  </p>
                </div>
                <span className="text-xs text-neutral-500 whitespace-nowrap">hace {timeAgo(e.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
